import Select, { SelectProps } from '@mui/material/Select';
import { useFieldContext } from '@/lib/form.ts';
import InputLabel from '@mui/material/InputLabel';
import { useId } from 'react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';
import Box from '@mui/material/Box';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: COMMON_VALIDATION_ERRORS.required, // It doesn't make sense to define .min() on select values other than to require it
};

export type FormSelectFieldProps = Omit<SelectProps, 'error' | 'value' | 'onChange' | 'onBlur'> & {
  errorMessage?: string;
};

export const FormSelectField = ({
  children,
  size = 'small',
  label,
  labelId,
  fullWidth = true,
  errorMessage,
  ...rest
}: FormSelectFieldProps) => {
  const field = useFieldContext();
  const generatedId = useId();
  const id = labelId ?? generatedId;

  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      disabled={rest.disabled}
      required={rest.required}
      error={shouldShowFieldErrors}
    >
      <InputLabel size={size} id={id} required={rest.required} error={shouldShowFieldErrors}>
        {label}
      </InputLabel>
      <Select
        {...rest}
        label={label}
        labelId={id}
        size={size}
        fullWidth={fullWidth}
        value={field.state.value === null ? '' : field.state.value}
        onChange={(e) => field.handleChange(e.target.value === '' ? null : e.target.value)}
        onBlur={handleBlur}
        error={shouldShowFieldErrors}
      >
        {children}
      </Select>
      {shouldShowFieldErrors && (
        <FormHelperText error>
          <FormFieldErrors zodIssueMap={zodIssueMap} />
          {errorMessage && (
            <Box data-testid={`${field.name}-error`} component="span" sx={{ display: 'block' }}>
              {errorMessage}
            </Box>
          )}
        </FormHelperText>
      )}
    </FormControl>
  );
};
